import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { formatPrice } from '@/lib/utils'
import type { Perfume } from '@/types'
import { ScentFamilyBadge } from './ScentFamilyBadge'
import { Badge } from '@/components/ui/Badge'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'

interface ProductCardProps {
  perfume: Perfume
  index?: number
}

export function ProductCard({ perfume, index = 0 }: ProductCardProps) {
  const addItem = useCartStore((s) => s.addItem)
  const addToast = useUIStore((s) => s.addToast)
  const pulseBag = useUIStore((s) => s.pulseBag)

  const lowestSize = perfume.sizes[0]
  if (!lowestSize) {
    return null
  }

  const anyInStock = perfume.sizes.some((s) => s.inStock !== false)
  const allOutOfStock = !anyInStock
  const lowStock =
    anyInStock &&
    !allOutOfStock &&
    perfume.sizes.reduce((sum, s) => sum + (s.stockQty ?? (s.inStock === false ? 0 : 10)), 0) < 10

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault()
    if (allOutOfStock) {
      addToast(`${perfume.name} is currently out of stock`, 'error')
      return
    }
    addItem(perfume, lowestSize.id)
    pulseBag()
    addToast(`Added ${perfume.name} to cart`, 'success')
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="group relative"
    >
      <Link to={`/product/${perfume.id}`} className="block">
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-card">
          <img
            src={perfume.images[0]}
            alt={perfume.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/10" />

          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            {perfume.isNew && <Badge variant="new">New</Badge>}
            {perfume.isBestseller && <Badge variant="bestseller">Bestseller</Badge>}
            {perfume.isDiscoverySet && <Badge variant="default">Discovery</Badge>}
            {allOutOfStock && <Badge variant="default">Out of stock</Badge>}
            {!allOutOfStock && lowStock && <Badge variant="default">Low stock</Badge>}
          </div>

          <button
            onClick={handleQuickAdd}
            disabled={allOutOfStock}
            className="absolute bottom-4 right-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 bg-ink px-4 py-2 text-xs normal-case tracking-[0.12em] text-white hover:bg-wd-orange disabled:cursor-not-allowed disabled:bg-ink/40"
          >
            Add
          </button>
        </div>

        <div className="mt-5 space-y-2">
          <div className="flex items-center justify-between">
            <ScentFamilyBadge family={perfume.family} />
            <span className="label-condensed text-carbon/50">{perfume.concentration}</span>
          </div>
          <h3 className="font-display text-2xl normal-case text-carbon transition-colors group-hover:text-wd-orange">
            {perfume.name.toLowerCase()}
          </h3>
          <p className="text-sm text-carbon/60">From {formatPrice(lowestSize.price)}</p>
        </div>
      </Link>
    </motion.div>
  )
}
